import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import client from '../contentful';
import Header from '../component/Header.js';
import About from '../component/About.js';
import Stats from '../component/Stats.js';
import HowIWork from '../component/HowIWork.js';
import Testimonials from '../component/Testimonials.js';

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const location = useLocation();

  useEffect(() => {
    client.getEntries({ content_type: 'project' })
      .then(response => setPosts(response.items.slice(0, 6)));
  }, []);

  // Auto-scroll carousel every 5 seconds
  useEffect(() => {
    if (posts.length === 0) return;
    
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % posts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [posts]);

  // Scroll to case-studies, projects, or about section if anchor is in URL
  useEffect(() => {
    const scrollToElement = () => {
      if (location.hash === '#case-studies' || location.hash === '#projects' || location.hash === '#about') {
        const targetId = location.hash.slice(1); // Remove the # from hash
        const element = document.getElementById(targetId);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    };

    scrollToElement();
  }, [location]);

  // Handle touch swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    setTouchEnd(e.changedTouches[0].clientX);
    handleSwipe();
  };

  const handleSwipe = () => {
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCarouselIndex((prev) => (prev + 1) % posts.length);
    }
    if (isRightSwipe) {
      setCarouselIndex((prev) => (prev - 1 + posts.length) % posts.length);
    }
  };

  return (
    <div>
      <Header />
      <div id="case-studies" className="md:flex md:flex-col space-y-8 bg-dj-light py-12 md:py-28 px-4 md:px-20">
        <div className="max-w-6xl mx-auto w-full">
          {/* Case Studies Section */}
          <div className="w-full mb-20 md:mb-28">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-secondary mb-4">Case Studies</h2>
            <h1 className="text-4xl md:text-6xl font-bold text-dj-dark mb-8">Deep Dives</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Tyson Case Study Card */}
              <Link to="/tyson" className="no-underline">
                <div className="bg-gradient-to-br from-dj-dark to-dj-dark/95 text-white rounded-lg overflow-hidden hover:shadow-2xl transition-all duration-300 h-full flex flex-col p-8 border-l-4 border-primary">
                  <p className="text-primary text-xs font-semibold uppercase tracking-widest mb-3">Portfolio Case Study</p>
                  <h3 className="text-2xl font-bold mb-4 leading-tight">Tyson Foods, Inc.</h3>
                  <p className="text-gray-300 mb-6 flex-grow">Unifying a fragmented digital ecosystem across 11 national consumer brands — managing platform strategy, content governance, design systems, and enterprise migration.</p>
                  
                  <div className="space-y-3 mb-6 pt-4 border-t border-gray-600">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Company</span>
                      <span className="font-semibold">Tyson Foods</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Role</span>
                      <span className="font-semibold">Digital & MarTech Lead</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Timeline</span>
                      <span className="font-semibold">April 2025 – Present</span>
                    </div>
                  </div>
                  
                  <button className="bg-primary hover:bg-secondary text-dj-dark font-bold py-2 px-4 rounded text-sm w-full transition-colors">
                    Read Case Study <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </button>
                </div>
              </Link>

              {/* femPAQ Case Study Card */}
              <Link to="/fempaq-case-study" className="no-underline">
                <div className="bg-white text-dj-dark rounded-lg overflow-hidden hover:shadow-2xl transition-all duration-300 h-full flex flex-col p-8 border-l-4 border-primary">
                  <p className="text-primary text-xs font-semibold uppercase tracking-widest mb-3">Portfolio Case Study</p>
                  <h3 className="text-2xl font-bold mb-4 leading-tight text-dj-dark">femPAQ Health</h3>
                  <p className="text-gray-700 mb-6 flex-grow">Designing an AI-powered women's health platform that serves two very different users — patients and providers — while maintaining a brand aesthetic that's approachable, not clinical.</p>
                  
                  <div className="space-y-3 mb-6 pt-4 border-t border-blue-200">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Company</span>
                      <span className="font-semibold text-dj-dark">femPAQ Inc.</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Role</span>
                      <span className="font-semibold text-dj-dark">Technical Co-Founder & CTO</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Timeline</span>
                      <span className="font-semibold text-dj-dark">2019 – Present</span>
                    </div>
                  </div>
                  
                  <button className="bg-primary hover:bg-secondary text-dj-dark font-bold py-2 px-4 rounded text-sm w-full transition-colors">
                    Read Case Study <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </button>
                </div>
              </Link>
            </div>
            <div id="projects"></div>
          </div>

          <div className="w-full mb-8 md:mb-12">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-secondary mb-4">Featured Work</h2>
            <h1 className="text-4xl md:text-6xl font-bold text-dj-dark">My Projects</h1>
          </div>
          {/* Desktop/Tablet Grid */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
            {posts.map(post => {
              const { slug, client: projectClient, title, description, heroImage, industry } = post.fields;
              const heroUrl = heroImage && heroImage.fields && heroImage.fields.file ? heroImage.fields.file.url : '';
              
              return (
                <Link to={`/post/${slug}`} key={slug} className="no-underline">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 cursor-pointer h-full flex flex-col">
                    {heroUrl && (
                      <img src={heroUrl} alt={projectClient || title} className="w-full h-56 object-cover hover:scale-105 transition-transform duration-300" />
                    )}
                    <div className="p-6 md:p-8 flex-1 flex flex-col">
                      <h3 className="text-xl font-semibold text-dj-dark mb-2">{projectClient || title}</h3>
                      {industry && <p className="text sm text-secondary font-semibold mb-3">{industry}</p>}
                      <p className="text-gray-700 text-md flex-grow mb-0">{description || 'Click to view more details'}</p>
                     
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile Carousel */}
          <div 
            className="sm:hidden relative mb-12 md:mb-16"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-300 ease-out" style={{ transform: `translateX(-${carouselIndex * 100}%)` }}>
                {posts.map(post => {
                  const { slug, client: projectClient, title, description, heroImage, industry } = post.fields;
                  const heroUrl = heroImage && heroImage.fields && heroImage.fields.file ? heroImage.fields.file.url : '';
                  
                  return (
                    <Link to={`/post/${slug}`} key={slug} className="no-underline w-full flex-shrink-0 px-4">
                      <div className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer h-full flex flex-col">
                        {heroUrl && (
                          <img src={heroUrl} alt={projectClient || title} className="w-full h-56 object-cover" />
                        )}
                        <div className="p-6 md:p-8 flex-1 flex flex-col">
                          <h3 className="text-lg font-bold text-dj-dark mb-2">{projectClient || title}</h3>
                          {industry && <p className="text-sm text-secondary font-semibold mb-3">{industry}</p>}
                          <p className="text-gray-700 text-sm flex-grow mb-6">{description || 'Click to view more details'}</p>
                          <button className="bg-primary hover:bg-secondary text-dj-dark font-bold py-2 px-4 rounded text-sm w-full">
                            View Project <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                          </button>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {posts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCarouselIndex(index)}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === carouselIndex ? 'bg-primary' : 'bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <Stats />
      <About />
      <HowIWork />
      <Testimonials />
    </div>
  );
};

export default Home;
